import moment from "moment";

export type DateFormat =
  | "MM/DD/YYYY" // US format (01/31/2023)
  | "DD/MM/YYYY" // European format (31/01/2023)
  | "YYYY-MM-DD" // ISO format (2023-01-31)
  | "DD-MMM-YYYY"; // Long format (31-Jan-2023)

// Default date format
export const DEFAULT_DATE_FORMAT: DateFormat = "MM/DD/YYYY";

/**
 * Format a date according to the provided format
 * @param date The date to format (Date object or string)
 * @param format The format to use
 * @param includeTime Whether to include time
 * @returns Formatted date string
 */
export const formatDate = (
  date: Date | string | number,
  format: DateFormat = DEFAULT_DATE_FORMAT,
  includeTime: boolean = false
): string => {
  if (!date) return "";

  try {
    if (includeTime) {
      return moment(date).format(`${format} HH:mm`);
    }
    return moment(date).format(format);
  } catch (error) {
    console.error("Error formatting date:", error);
    return String(date);
  }
};

/**
 * Format a date using the browser's toLocaleDateString
 * This is used as a fallback when moment is not available
 * @param date The date to format
 * @param format The format to approximate with toLocaleDateString
 * @returns Formatted date string
 */
export const formatDateNative = (
  date: Date | string | number,
  format: DateFormat = DEFAULT_DATE_FORMAT
): string => {
  if (!date) return "";

  try {
    const dateObj = new Date(date);

    // Map our format strings to native locale options
    let options: Intl.DateTimeFormatOptions = {};
    let locale = "en-US"; // Default

    switch (format) {
      case "DD/MM/YYYY":
        locale = "en-GB"; // British English uses day/month/year
        options = { day: "2-digit", month: "2-digit", year: "numeric" };
        break;
      case "YYYY-MM-DD":
        options = { year: "numeric", month: "2-digit", day: "2-digit" };
        // We'll need to manually reformat the result for this case
        break;
      case "DD-MMM-YYYY":
        options = { day: "2-digit", month: "short", year: "numeric" };
        break;
      case "MM/DD/YYYY":
      default:
        options = { month: "2-digit", day: "2-digit", year: "numeric" };
        break;
    }

    let result = dateObj.toLocaleDateString(locale, options);

    // For YYYY-MM-DD format, we need to manually reformat
    if (format === "YYYY-MM-DD") {
      const parts = result.split("/");
      if (parts.length === 3) {
        // For US locale, reorder from MM/DD/YYYY to YYYY-MM-DD
        result = `${parts[2]}-${parts[0].padStart(2, "0")}-${parts[1].padStart(2, "0")}`;
      }
    }

    return result;
  } catch (error) {
    console.error("Error formatting date with native method:", error);
    return String(date);
  }
};
